import Iconify from 'src/components/Iconify';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'src/@/components/ui/card';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';

const LABEL_CLS = 'text-xs font-semibold uppercase tracking-wider text-gray-500';
const HINT_CLS = 'text-xs text-gray-400';
const INPUT_CLS = 'rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20';

// Defaults must mirror lais-ai/src/agents/realestate/identity.js → DEFAULT_AGENT_CONFIG
const DEFAULTS = {
    filtroStatus: 'Vago/Disponível',
    limiteBusca: 15,
    maxSugestoes: 3,
    maxTentativasRelax: 3,
    rodadasParaLinkCompleto: 2,
    maxImoveisLink: 20,
    propertyUrlPattern: 'https://www.psimobiliaria.com.br/imovel/[FINALIDADE]/[ID]',
    priceRanking: 'cheapest',
    suggestionStyle: 'detailed_balloons',
};

const PRICE_RANKING_OPCOES = [
    { value: 'cheapest', label: 'Mais baratos primeiro' },
    { value: 'closest_to_budget', label: 'Próximos do orçamento do cliente' },
    { value: 'largest_area', label: 'Maior área dentro do orçamento' },
];

const SUGGESTION_STYLE_OPCOES = [
    { value: 'detailed_balloons', label: 'Cards com ícones (1 mensagem por imóvel)' },
    { value: 'compact_inline', label: 'Lista compacta inline' },
];

// Clamp a numeric input to [min, max]; returns undefined when empty so the
// field keeps using the default from the agent runtime.
const coerceInt = (value, min, max) => {
    if (value === '' || value === null || value === undefined) return undefined;
    const n = Number(value);
    if (!Number.isFinite(n)) return undefined;
    const i = Math.trunc(n);
    return Math.max(min, Math.min(max, i));
};

export default function BuscaCatalogoSection({ aiAgent, changeModuleConfig }) {
    const busca = aiAgent?.config?.module?.realstate?.busca || {};

    const updateBusca = (patch) => {
        changeModuleConfig({
            busca: {
                ...busca,
                ...patch,
            },
        });
    };

    return (
        <Card className="rounded-xl border border-gray-100 bg-white">
            <CardHeader>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-50">
                        <Iconify icon="mdi:magnify" width={20} className="text-violet-600" />
                    </div>
                    <div>
                        <CardTitle>Busca & Catálogo</CardTitle>
                        <CardDescription>Parâmetros operacionais da busca de imóveis e do fluxo de sugestões.</CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="flex flex-col gap-5">
                <div className="flex flex-col gap-2">
                    <Label className={LABEL_CLS}>Status do imóvel para considerar disponível</Label>
                    <Input
                        value={busca.filtroStatus ?? ''}
                        onChange={(e) => updateBusca({ filtroStatus: e.target.value })}
                        placeholder={DEFAULTS.filtroStatus}
                        className={INPUT_CLS}
                    />
                    <p className={HINT_CLS}>Valor exato do campo <code>status</code> na coleção de imóveis. Padrão: <code>{DEFAULTS.filtroStatus}</code>.</p>
                </div>

                <div className="flex flex-col gap-2">
                    <Label className={LABEL_CLS}>URL pública do imóvel</Label>
                    <Input
                        value={busca.propertyUrlPattern ?? ''}
                        onChange={(e) => updateBusca({ propertyUrlPattern: e.target.value })}
                        placeholder={DEFAULTS.propertyUrlPattern}
                        className={INPUT_CLS}
                    />
                    <p className={HINT_CLS}>
                        Padrão de link enviado ao cliente quando a Lais apresenta um imóvel. Use os placeholders{' '}
                        <code>[codigo do imovel]</code> (ou <code>[ID]</code>) e, se necessário,{' '}
                        <code>[FINALIDADE]</code> (vira <code>aluguel</code> ou <code>venda</code>). Ex.:{' '}
                        <code>https://www.psimobiliaria.com.br/imovel/interno/[codigo do imovel]</code>.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>Limite da busca no banco</Label>
                        <Input
                            type="number"
                            min={1}
                            max={100}
                            value={busca.limiteBusca ?? ''}
                            onChange={(e) => updateBusca({ limiteBusca: coerceInt(e.target.value, 1, 100) })}
                            placeholder={String(DEFAULTS.limiteBusca)}
                            className={INPUT_CLS}
                        />
                        <p className={HINT_CLS}>Máximo de imóveis retornados do MongoDB por query. Padrão: {DEFAULTS.limiteBusca}.</p>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>Sugestões por resposta</Label>
                        <Input
                            type="number"
                            min={1}
                            max={10}
                            value={busca.maxSugestoes ?? ''}
                            onChange={(e) => updateBusca({ maxSugestoes: coerceInt(e.target.value, 1, 10) })}
                            placeholder={String(DEFAULTS.maxSugestoes)}
                            className={INPUT_CLS}
                        />
                        <p className={HINT_CLS}>Quantos imóveis a Lais apresenta ao cliente por rodada. Padrão: {DEFAULTS.maxSugestoes}.</p>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>Tentativas de relaxamento</Label>
                        <Input
                            type="number"
                            min={1}
                            max={10}
                            value={busca.maxTentativasRelax ?? ''}
                            onChange={(e) => updateBusca({ maxTentativasRelax: coerceInt(e.target.value, 1, 10) })}
                            placeholder={String(DEFAULTS.maxTentativasRelax)}
                            className={INPUT_CLS}
                        />
                        <p className={HINT_CLS}>Quantas vezes a Lais tenta relaxar filtros antes de admitir que não achou nada. Padrão: {DEFAULTS.maxTentativasRelax}.</p>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>Rodadas até link completo</Label>
                        <Input
                            type="number"
                            min={1}
                            max={10}
                            value={busca.rodadasParaLinkCompleto ?? ''}
                            onChange={(e) => updateBusca({ rodadasParaLinkCompleto: coerceInt(e.target.value, 1, 10) })}
                            placeholder={String(DEFAULTS.rodadasParaLinkCompleto)}
                            className={INPUT_CLS}
                        />
                        <p className={HINT_CLS}>Depois de N rodadas de sugestões, a Lais envia um link com todos os imóveis. Padrão: {DEFAULTS.rodadasParaLinkCompleto}.</p>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>Máximo de imóveis no link</Label>
                        <Input
                            type="number"
                            min={1}
                            max={200}
                            value={busca.maxImoveisLink ?? ''}
                            onChange={(e) => updateBusca({ maxImoveisLink: coerceInt(e.target.value, 1, 200) })}
                            placeholder={String(DEFAULTS.maxImoveisLink)}
                            className={INPUT_CLS}
                        />
                        <p className={HINT_CLS}>Quantos imóveis no máximo entram no link completo enviado ao cliente. Padrão: {DEFAULTS.maxImoveisLink}.</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>Como ordenar imóveis sugeridos</Label>
                        <Select
                            value={busca.priceRanking || DEFAULTS.priceRanking}
                            onValueChange={(value) => updateBusca({ priceRanking: value })}
                        >
                            <SelectTrigger className={INPUT_CLS}>
                                <SelectValue placeholder="Selecione" />
                            </SelectTrigger>
                            <SelectContent>
                                {PRICE_RANKING_OPCOES.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className={HINT_CLS}>
                            Define a ordenação dos imóveis na busca. <strong>Próximos do orçamento</strong> mostra opções perto do teto que o cliente informou (ex.: cliente disse R$ 2.500 → mostra R$ 2.500/2.200/2.200, não R$ 350).
                        </p>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>Estilo das mensagens de sugestão</Label>
                        <Select
                            value={busca.suggestionStyle || DEFAULTS.suggestionStyle}
                            onValueChange={(value) => updateBusca({ suggestionStyle: value })}
                        >
                            <SelectTrigger className={INPUT_CLS}>
                                <SelectValue placeholder="Selecione" />
                            </SelectTrigger>
                            <SelectContent>
                                {SUGGESTION_STYLE_OPCOES.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className={HINT_CLS}>
                            <strong>Cards</strong>: bolha separada por imóvel com 5 linhas (preço, localização, área, banheiros, destaques). <strong>Lista compacta</strong>: uma linha por imóvel com link inline (estilo "Loja de 45m² por R$ 2.500: link").
                        </p>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
